import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, CopyToClipboardButton } from '@frontend/design-system';
import { DATETIME_FORMATS, PRIMARY_EVENT_COLOR } from '../../../constants';
import { useGetEntityArchiveStatus, useSchedulingLocationInfo } from '../../../hooks';
import { stripDateOrdinals } from '../../../utils';
import { OverlappingEventIndicator } from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { AppointmentStatusV2 } from '../../schedule-calendar-components/appointment-event-card/appointment-status-v2';
import { EventCardAppointmentStatusChipV3 } from '../../v3/EventCardAppointmentStatusChip';
import { ColorDot } from '../color-dot';
import { useApptPanelContext } from './appt-panel-context';
import { DetailItem } from './item-detail';
import { PanelTabs } from './panel-tabs';
import { useGetLocationAddress } from './use-get-location-address';

export const PanelDetails = () => {
  const { t } = useTranslation('schedule');
  const { getScopeName, locationData, isMultiLocation } = useSchedulingLocationInfo();
  const { apptDetails, isV3Enabled, locationId, shouldShowOperatory } = useApptPanelContext();
  const details = apptDetails?.appointmentDetails;

  const { isAppointmentTypeArchived, isProviderArchived } = useGetEntityArchiveStatus({
    appointmentTypeId: details?.appointmentTypeId || '',
    providerId: details?.providerIds?.[0] || '',
    locationId: locationId,
  });
  const locationAddressText = useGetLocationAddress(locationData[locationId]?.slug);

  const apptId = apptDetails?.id ?? '';
  const apptTypeName = details?.type || '';
  const apptTypeColor = details?.displayOptions?.primaryColor || PRIMARY_EVENT_COLOR;
  const providerName = details?.practitioner ?? '';
  const operatoryName = details?.operatory || '';
  const status = details?.status;
  const startDate = details?.date ? formatDate(stripDateOrdinals(details?.date), DATETIME_FORMATS.ISO_DATE) : '';
  const referenceId = details?.referenceId || '';

  return (
    <div css={contentContainer}>
      <div css={{ alignSelf: 'flex-end' }}>
        {status &&
          (isV3Enabled ? (
            <EventCardAppointmentStatusChipV3
              locationId={locationId}
              appointmentId={apptId}
              appointmentStatus={status}
              appointmentStartDate={startDate}
              appointmentReferenceId={referenceId}
            />
          ) : (
            <AppointmentStatusV2 locationId={locationId} appointmentId={apptId} status={status} />
          ))}
      </div>
      {(isMultiLocation || !!locationAddressText) && (
        <DetailItem
          title={getScopeName(locationId)}
          leftElement={<Icon name='location-small' color='light' />}
          rightElement={
            !!locationAddressText && (
              <CopyToClipboardButton
                css={{ height: 24, width: 24 }}
                variant='tertiary'
                textToCopy={locationAddressText}
                hasSuccessTooltip
                tooltipSuccessText={t('Copied')}
              />
            )
          }
        >
          {!!locationAddressText && (
            <Text size='small' color='light'>
              {locationAddressText}
            </Text>
          )}
        </DetailItem>
      )}
      <DetailItem
        title={formatDate(startDate, 'ddd, MMMM DD, YYYY')}
        leftElement={<Icon name='calendar-small' color='light' />}
        rightElement={
          isV3Enabled && <OverlappingEventIndicator variant='full' visible={details?.isOverlapping} borderless />
        }
      >
        <Text size='small' color='light'>
          {details?.duration}
        </Text>
      </DetailItem>
      {!!apptTypeName && (
        <DetailItem
          isArchived={isAppointmentTypeArchived}
          title={apptTypeName}
          leftElement={<ColorDot color={apptTypeColor} />}
        />
      )}
      {shouldShowOperatory && !!operatoryName && (
        <DetailItem title={operatoryName} leftElement={<Icon name='department-small' color='light' />} />
      )}
      {!!providerName && (
        <DetailItem
          title={providerName}
          isArchived={isProviderArchived}
          leftElement={<Icon name='user-small' color='light' />}
        />
      )}
      <PanelTabs />
    </div>
  );
};

const contentContainer = css({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  gap: theme.spacing(1),
});
